import { Component } from 'react';
import { Sun, Moon, RefreshCcw, Home, AlertOctagon, Copy, Check } from 'lucide-react';
import { DEFAULT_LOGO_URL } from './landing/landingConstants';

/**
 * Top-level error boundary with a branded "Red Card" fallback.
 *
 * Catches any uncaught render-time error anywhere in the app and shows a
 * coaching-themed 500 page instead of the blank white screen of death.
 * Hooks into Sentry if the global `window.Sentry` is wired (no-op otherwise).
 */
class ErrorBoundaryClass extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      info: null,
      // Theme read at construction so the fallback never depends on app state
      // (the app might be in a broken state when we render).
      theme: (typeof window !== 'undefined' && localStorage.getItem('landingTheme')) || 'dark',
      copied: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to the browser console for local debugging
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught:', error, info);
    this.setState({ info });

    // Optional Sentry hand-off — only fires if Sentry SDK is loaded on
    // window. No hard dep so the boundary works before Sentry is wired.
    try {
      if (typeof window !== 'undefined' && window.Sentry?.captureException) {
        window.Sentry.captureException(error, {
          extra: { componentStack: info?.componentStack },
        });
      }
    } catch (_) { /* never let logging break the boundary */ }
  }

  reset = () => {
    this.setState({ hasError: false, error: null, info: null, copied: false });
  };

  reload = () => {
    window.location.reload();
  };

  goHome = () => {
    const isLoggedIn = !!localStorage.getItem('token');
    window.location.href = isLoggedIn ? '/dressing-room' : '/';
  };

  toggleTheme = () => {
    const next = this.state.theme === 'dark' ? 'light' : 'dark';
    this.setState({ theme: next });
    try { localStorage.setItem('landingTheme', next); } catch (_) {}
  };

  copyError = async () => {
    const { error, info } = this.state;
    const text = [
      `Error: ${error?.name || 'Error'}: ${error?.message || 'Unknown'}`,
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      info?.componentStack ? `\nComponent stack:${info.componentStack}` : '',
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 1800);
    } catch (_) {}
  };

  render() {
    // Dev/design preview hatch: append `?_red_card_preview=1` to any URL to
    // render the Red Card fallback without triggering a real crash. Useful
    // for design review without throwing real errors.
    const previewMode = typeof window !== 'undefined' &&
      window.location.search.includes('_red_card_preview=1');
    if (!this.state.hasError && !previewMode) return this.props.children;

    const { theme, error, copied } = this.state;
    const isDark = theme === 'dark';
    const bg = isDark ? 'bg-[#0a0e14]' : 'bg-gray-50';
    const text = isDark ? 'text-white' : 'text-gray-900';
    const subtext = isDark ? 'text-gray-400' : 'text-gray-600';
    const mutedText = isDark ? 'text-gray-500' : 'text-gray-500';

    return (
      <div className={`min-h-screen ${bg} ${text} relative overflow-hidden`} data-testid="error-boundary-fallback">
        <style>{`
          @keyframes ref-pulse {
            0%, 100% { transform: scale(1); }
            50%      { transform: scale(1.03); }
          }
          .ref-img { animation: ref-pulse 3.2s ease-in-out infinite; }
          .pitch-stripe-red::before {
            content: "";
            position: absolute;
            inset: 0;
            background: repeating-linear-gradient(
              90deg,
              rgba(220, 38, 38, 0.05) 0px,
              rgba(220, 38, 38, 0.05) 60px,
              transparent 60px,
              transparent 120px
            );
            pointer-events: none;
          }
        `}</style>

        {/* Theme toggle */}
        <button
          onClick={this.toggleTheme}
          className={`fixed bottom-6 left-6 z-50 p-3 rounded-full shadow-lg transition-all ${
            isDark ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' : 'bg-[#007a33] text-white hover:bg-[#005c26]'
          }`}
          aria-label="Toggle theme"
          data-testid="theme-toggle"
        >
          {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        {/* Logo top-left */}
        <button
          onClick={this.goHome}
          className="absolute top-6 left-6 z-20 flex items-center gap-2 group"
          data-testid="error-logo-btn"
        >
          <img
            src={DEFAULT_LOGO_URL}
            alt="Grassroots Coaching"
            className="w-10 h-10 object-contain group-hover:opacity-80 transition-opacity"
          />
          <span className={`font-bold ${text} hidden sm:inline`}>Grassroots Coaching</span>
        </button>

        <main className="relative pitch-stripe-red min-h-screen flex items-center justify-center px-4 py-20">
          <div className="max-w-3xl w-full text-center relative z-10">
            {/* Pre-headline match-card style label */}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30 mb-6">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs font-semibold tracking-widest uppercase text-red-500">
                Match report · 500
              </span>
            </div>

            {/* Referee with red card visual */}
            <div className="flex items-center justify-center mb-8 select-none">
              <img
                src="https://customer-assets.emergentagent.com/job_b281913e-4091-4a90-899a-c0e767ce9196/artifacts/pv5m87qi_Untitled%20design%20-%202026-06-05T192736.798.png"
                alt="Referee showing a red card"
                className="w-[220px] h-[220px] sm:w-[280px] sm:h-[280px] object-cover rounded-full ref-img"
                draggable="false"
                data-testid="error-referee-img"
              />
            </div>

            {/* Red Card headline */}
            <h1 className={`text-4xl sm:text-6xl font-black tracking-tight mb-4 ${text}`}>
              Red Card.
            </h1>

            <p className={`text-base sm:text-lg max-w-xl mx-auto mb-2 ${subtext}`}>
              That was on us — the ref's pulled out a red card on our end.
            </p>
            <p className={`text-sm max-w-xl mx-auto mb-8 ${mutedText}`}>
              The bench has been notified and the assistants are reviewing the tape.
            </p>

            {/* Error console */}
            {error && (
              <div
                className={`inline-block mb-8 px-4 py-3 rounded-md font-mono text-xs sm:text-sm border max-w-2xl text-left ${
                  isDark
                    ? 'bg-gray-900/60 border-red-900/50 text-gray-400'
                    : 'bg-red-50 border-red-200 text-gray-600'
                }`}
                data-testid="error-details"
              >
                <div className="flex items-start gap-2">
                  <AlertOctagon className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <div className="flex-1 break-words">
                    <span className={isDark ? 'text-red-400' : 'text-red-700'}>{error.name || 'Error'}:</span>
                    {' '}
                    <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                      {error.message || 'Something went wrong'}
                    </span>
                  </div>
                  <button
                    onClick={this.copyError}
                    className={`shrink-0 text-xs flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                      isDark
                        ? 'hover:bg-gray-800 text-gray-400 hover:text-white'
                        : 'hover:bg-red-100 text-gray-500 hover:text-gray-900'
                    }`}
                    title="Copy diagnostic details"
                    data-testid="copy-error-btn"
                  >
                    {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            )}

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={this.reload}
                className="inline-flex items-center justify-center gap-2 bg-[#007a33] hover:bg-[#005c26] text-white px-6 py-3 rounded-md font-medium transition-colors"
                data-testid="error-reload-btn"
              >
                <RefreshCcw className="w-4 h-4" />
                Restart the match
              </button>
              <button
                onClick={this.goHome}
                className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md font-medium border transition-colors ${
                  isDark
                    ? 'border-gray-700 text-white hover:bg-gray-800'
                    : 'border-gray-300 text-gray-900 hover:bg-gray-100'
                }`}
                data-testid="error-home-btn"
              >
                <Home className="w-4 h-4" />
                Back to the touchline
              </button>
            </div>

            <p className={`text-xs mt-10 ${mutedText}`}>
              If this keeps happening, hit{' '}
              <span className={isDark ? 'text-white' : 'text-gray-900'}>Copy</span>{' '}
              and{' '}
              <a
                href="mailto:support@grassrootscoaching.app?subject=Red%20Card%20-%20App%20Error"
                className="text-[#007a33] hover:underline font-medium"
                data-testid="error-support-link"
              >
                send the gaffer
              </a>{' '}
              the diagnostics.
            </p>
          </div>
        </main>
      </div>
    );
  }
}

export const ErrorBoundary = ErrorBoundaryClass;
export default ErrorBoundaryClass;
